// src/pages/salas/SalasAdmin.jsx — configuración del módulo (solo admin del Desk).
// Recepcionistas, excepciones de acceso, criterios por puesto/unidad, horario
// y catálogo de salas. Todo se guarda vía PUT /salas/settings y PUT /salas/rooms.
import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useSalas, I } from './SalasShell';
import { UserPicker } from './SalasHome';

const DAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

export default function SalasAdmin() {
    const { access, reload } = useSalas();
    const [cfg, setCfg] = useState(null);
    const [rooms, setRooms] = useState([]);
    const [saving, setSaving] = useState(false);
    const [levelText, setLevelText] = useState('');
    const [corpText, setCorpText] = useState('');
    const [canEditManagers, setCanEditManagers] = useState(false);

    useEffect(() => {
        api.get('/salas/settings')
            .then(({ data }) => {
                setCfg(data.settings); setRooms(data.rooms); setCanEditManagers(!!data.canEditManagers);
                setLevelText((data.settings.levelKeywords || []).join(', '));
                setCorpText((data.settings.corporateKeywords || []).join(', '));
            })
            .catch(e => toast.error(e.response?.data?.error || 'No se pudo cargar la configuración'));
    }, []);

    if (!access?.isModuleAdmin) return <div className="sl-alert err">Solo administradores del módulo.</div>;
    if (!cfg) return <div className="sl-center"><Spinner animation="border" /></div>;

    const split = (s) => s.split(',').map(x => x.trim()).filter(Boolean);
    const addTo = (field, u) => {
        if (!u?._id) return;
        if ((cfg[field] || []).some(x => String(x._id) === String(u._id))) return;
        setCfg({ ...cfg, [field]: [...(cfg[field] || []), u] });
    };
    const removeFrom = (field, id) => setCfg({ ...cfg, [field]: cfg[field].filter(x => String(x._id) !== String(id)) });
    const toggleDay = (d) => {
        const has = cfg.weekdays.includes(d);
        setCfg({ ...cfg, weekdays: has ? cfg.weekdays.filter(x => x !== d) : [...cfg.weekdays, d].sort() });
    };

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                receptionists: cfg.receptionists.map(u => u._id),
                extraAllowed:  cfg.extraAllowed.map(u => u._id),
                ...(canEditManagers ? { managers: (cfg.managers || []).map(u => u._id) } : {}),
                levelKeywords: split(levelText),
                corporateKeywords: split(corpText),
                deskLeadersCount: !!cfg.deskLeadersCount,
                openTime: cfg.openTime, closeTime: cfg.closeTime,
                slotMinutes: cfg.slotMinutes, minDurationMin: cfg.minDurationMin, maxDurationMin: cfg.maxDurationMin,
                maxDaysAhead: cfg.maxDaysAhead, weekdays: cfg.weekdays,
            };
            const [s, r] = await Promise.all([api.put('/salas/settings', body), api.put('/salas/rooms', { rooms })]);
            setCfg(s.data); setRooms(r.data);
            toast.success('Configuración guardada');
            reload();
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo guardar');
        } finally { setSaving(false); }
    };

    const setRoom = (i, patch) => setRooms(rooms.map((r, idx) => idx === i ? { ...r, ...patch } : r));

    return (
        <>
            <div className="sl-toolbar">
                <div>
                    <div className="sl-date-label" style={{ textTransform: 'none' }}>Configuración</div>
                    <div className="sl-date-sub">Quién administra, quién puede reservar y en qué horario.</div>
                </div>
            </div>

            <div className="sl-admin-grid">
                <div className="sl-card">
                    <h3>Recepcionistas</h3>
                    <p className="desc">Pueden reservar a nombre de cualquier persona, mover y cancelar cualquier reservación e imprimir los QR.</p>
                    <div className="sl-chips" style={{ marginBottom: 10 }}>
                        {cfg.receptionists.map(u => (
                            <span key={u._id} className="sl-chip">{u.name}<span className="x" onClick={() => removeFrom('receptionists', u._id)}>{I.x}</span></span>
                        ))}
                        {!cfg.receptionists.length && <span className="sl-muted">Nadie todavía.</span>}
                    </div>
                    <UserPicker value={null} text="" onPick={u => addTo('receptionists', u)} onText={() => {}} placeholder="Buscar persona para agregar…" clearOnPick />
                </div>

                <div className="sl-card">
                    <h3>Administradores del módulo</h3>
                    <p className="desc">Entran a esta Configuración (salas, horario, recepcionistas, excepciones) sin ser administradores del Desk. Solo un administrador del Desk puede cambiar esta lista.</p>
                    <div className="sl-chips" style={{ marginBottom: 10 }}>
                        {(cfg.managers || []).map(u => (
                            <span key={u._id} className="sl-chip">{u.name}{canEditManagers && <span className="x" onClick={() => removeFrom('managers', u._id)}>{I.x}</span>}</span>
                        ))}
                        {!(cfg.managers || []).length && <span className="sl-muted">Solo administradores del Desk.</span>}
                    </div>
                    {canEditManagers && <UserPicker value={null} text="" onPick={u => addTo('managers', u)} onText={() => {}} placeholder="Buscar persona para agregar…" clearOnPick />}
                </div>

                <div className="sl-card">
                    <h3>Excepciones de acceso</h3>
                    <p className="desc">Personas que pueden reservar aunque su puesto o unidad no cumpla los criterios.</p>
                    <div className="sl-chips" style={{ marginBottom: 10 }}>
                        {cfg.extraAllowed.map(u => (
                            <span key={u._id} className="sl-chip">{u.name}<span className="x" onClick={() => removeFrom('extraAllowed', u._id)}>{I.x}</span></span>
                        ))}
                        {!cfg.extraAllowed.length && <span className="sl-muted">Ninguna.</span>}
                    </div>
                    <UserPicker value={null} text="" onPick={u => addTo('extraAllowed', u)} onText={() => {}} placeholder="Buscar persona para agregar…" clearOnPick />
                </div>

                <div className="sl-card">
                    <h3>Quién puede reservar</h3>
                    <p className="desc">Se compara contra el puesto y la unidad/sucursal que trae el intranet (sin acentos ni mayúsculas). Separa con comas.</p>
                    <div className="sl-field">
                        <label className="sl-label">Palabras de nivel en el puesto</label>
                        <input className="sl-input" value={levelText} onChange={e => setLevelText(e.target.value)} placeholder="lider, gerente, subdirector, director" />
                        <div className="sl-hint">"director" también cubre "directora"; "lider" cubre "líder de proyecto".</div>
                    </div>
                    <div className="sl-field">
                        <label className="sl-label">Palabras de corporativo en unidad o sucursal</label>
                        <input className="sl-input" value={corpText} onChange={e => setCorpText(e.target.value)} placeholder="corporativo" />
                        <div className="sl-hint">Déjalo vacío para no exigir que sean de corporativo.</div>
                    </div>
                    <label className="sl-chip" style={{ cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!cfg.deskLeadersCount} onChange={e => setCfg({ ...cfg, deskLeadersCount: e.target.checked })} />
                        Los líderes de área del Desk cuentan como nivel líder
                    </label>
                </div>

                <div className="sl-card">
                    <h3>Horario y reglas</h3>
                    <p className="desc">Ventana en la que se puede reservar y límites por reservación.</p>
                    <div className="sl-row2">
                        <div className="sl-field"><label className="sl-label">Abre</label><input type="time" className="sl-input" value={cfg.openTime} onChange={e => setCfg({ ...cfg, openTime: e.target.value })} /></div>
                        <div className="sl-field"><label className="sl-label">Cierra</label><input type="time" className="sl-input" value={cfg.closeTime} onChange={e => setCfg({ ...cfg, closeTime: e.target.value })} /></div>
                    </div>
                    <div className="sl-row2">
                        <div className="sl-field">
                            <label className="sl-label">Intervalo</label>
                            <select className="sl-select" value={cfg.slotMinutes} onChange={e => setCfg({ ...cfg, slotMinutes: Number(e.target.value) })}>
                                {[15, 30, 60].map(v => <option key={v} value={v}>{v} min</option>)}
                            </select>
                        </div>
                        <div className="sl-field"><label className="sl-label">Anticipación máx. (días)</label><input type="number" min="1" max="365" className="sl-input" value={cfg.maxDaysAhead} onChange={e => setCfg({ ...cfg, maxDaysAhead: Number(e.target.value) })} /></div>
                    </div>
                    <div className="sl-row2">
                        <div className="sl-field"><label className="sl-label">Duración mínima (min)</label><input type="number" min="5" max="480" className="sl-input" value={cfg.minDurationMin} onChange={e => setCfg({ ...cfg, minDurationMin: Number(e.target.value) })} /></div>
                        <div className="sl-field"><label className="sl-label">Duración máxima (min)</label><input type="number" min="15" max="1440" className="sl-input" value={cfg.maxDurationMin} onChange={e => setCfg({ ...cfg, maxDurationMin: Number(e.target.value) })} /></div>
                    </div>
                    <label className="sl-label">Días</label>
                    <div className="sl-days">
                        {DAYS.map((d, i) => <button key={i} type="button" className={`sl-day ${cfg.weekdays.includes(i) ? 'on' : ''}`} onClick={() => toggleDay(i)}>{d}</button>)}
                    </div>
                </div>

                <div className="sl-card" style={{ gridColumn: '1 / -1' }}>
                    <h3>Salas</h3>
                    <p className="desc">Nombre, capacidad y ubicación. Desactiva una sala para que deje de aparecer sin perder su historial.</p>
                    <div className="sl-room-row" style={{ fontSize: 11.5, color: 'var(--c-text-4)', fontWeight: 600 }}>
                        <span>Nombre</span><span>Cupo</span><span className="loc">Ubicación</span><span>Color</span><span>Activa</span>
                    </div>
                    {rooms.map((r, i) => (
                        <div key={r._id || i} className="sl-room-row">
                            <input className="sl-input" value={r.name} onChange={e => setRoom(i, { name: e.target.value })} />
                            <input type="number" min="1" max="200" className="sl-input" value={r.capacity} onChange={e => setRoom(i, { capacity: Number(e.target.value) })} />
                            <input className="sl-input loc" value={r.location || ''} onChange={e => setRoom(i, { location: e.target.value })} placeholder="Piso 3" />
                            <input type="color" value={r.color || '#0a4aa8'} onChange={e => setRoom(i, { color: e.target.value })} style={{ width: 36, height: 36, border: 'none', background: 'none', cursor: 'pointer' }} />
                            <input type="checkbox" checked={r.active !== false} onChange={e => setRoom(i, { active: e.target.checked })} style={{ width: 18, height: 18 }} />
                        </div>
                    ))}
                    <button type="button" className="sl-btn sl-btn-ghost sm" onClick={() => setRooms([...rooms, { name: `Sala ${rooms.length + 1}`, capacity: 8, location: '', color: '#0a4aa8', active: true }])}>{I.plus} Agregar sala</button>
                </div>
            </div>

            <div className="sl-sticky-save">
                <button className="sl-btn sl-btn-primary" onClick={save} disabled={saving}>{saving ? <Spinner size="sm" animation="border" /> : 'Guardar cambios'}</button>
            </div>
        </>
    );
}
